export type EcountConfig = {
  comCode: string;
  userId: string;
  apiCertKey: string;
  lanType: string;
  zoneUrl: string;
  apiHostPrefix: string;
};

export type EcountInventoryRequest = {
  baseDate: string;
  whCd?: string;
  prodCd?: string;
  balFlag?: "Y" | "N";
  delGubun?: "Y" | "N";
  delLocationYn?: "Y" | "N";
};

export type EcountWarehouseInventoryFlowResult = {
  zone: string;
  sessionId: string;
  zoneResponse: any;
  loginResponse: any;
  inventoryResponse: any;
};

const DEFAULT_ZONE_URL = "https://oapi.ecount.com/OAPI/V2/Zone";
const DEFAULT_API_HOST_PREFIX = "https://oapi";

const normalizeHostPrefix = (value: string) => value.replace(/\/+$/, "");

const getPathValue = (source: any, path: string) =>
  path
    .split(".")
    .reduce<any>((acc, key) => (acc == null ? undefined : acc[key]), source);

const pickFirstValue = (source: any, paths: string[]) => {
  for (const path of paths) {
    const value = getPathValue(source, path);
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return null;
};

const pickFirstString = (source: any, paths: string[]) => {
  const value = pickFirstValue(source, paths);
  if (value == null) return null;
  const text = String(value).trim();
  return text ? text : null;
};

const pickFirstArray = (source: any, paths: string[]) => {
  for (const path of paths) {
    const value = getPathValue(source, path);
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
};

const extractErrorMessage = (source: any) => {
  return (
    pickFirstString(source, [
      "Error.MessageDetail",
      "Error.Message",
      "Error.Code",
      "error.message",
      "message",
    ]) || ""
  );
};

const parseJsonResponse = async (response: Response) => {
  const text = await response.text();
  if (!text.trim()) return {};

  try {
    return JSON.parse(text);
  } catch (_) {
    throw new Error(`JSON 응답 파싱에 실패했습니다. 원본: ${text.slice(0, 300)}`);
  }
};

const postJson = async (url: string, body: Record<string, unknown>) => {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });

  const data = await parseJsonResponse(response);
  if (!response.ok) {
    const errorMessage = extractErrorMessage(data);
    throw new Error(
      errorMessage || `HTTP ${response.status} ${response.statusText}`
    );
  }

  return data;
};

const ensureSuccess = (stepName: string, response: any) => {
  const status = pickFirstString(response, ["Status", "status"]);
  const errorCode = pickFirstString(response, ["Error.Code"]);
  const errorMessage = extractErrorMessage(response);

  if ((status && status !== "200") || errorCode) {
    throw new Error(
      errorMessage || `${stepName} 호출에 실패했습니다. 상태: ${status || "-"}`
    );
  }
};

export const getEcountConfigFromEnv = (): EcountConfig => ({
  comCode: String(process.env.REACT_APP_ECOUNT_COM_CODE || "").trim(),
  userId: String(process.env.REACT_APP_ECOUNT_USER_ID || "").trim(),
  apiCertKey: String(process.env.REACT_APP_ECOUNT_API_CERT_KEY || "").trim(),
  lanType: String(process.env.REACT_APP_ECOUNT_LAN_TYPE || "ko-KR").trim(),
  zoneUrl: String(
    process.env.REACT_APP_ECOUNT_ZONE_URL || DEFAULT_ZONE_URL
  ).trim(),
  apiHostPrefix: normalizeHostPrefix(
    String(
      process.env.REACT_APP_ECOUNT_API_HOST_PREFIX || DEFAULT_API_HOST_PREFIX
    ).trim()
  ),
});

export const getMissingEcountConfigFields = (config: EcountConfig) => {
  const missing: string[] = [];
  if (!config.comCode) missing.push("REACT_APP_ECOUNT_COM_CODE");
  if (!config.userId) missing.push("REACT_APP_ECOUNT_USER_ID");
  if (!config.apiCertKey) missing.push("REACT_APP_ECOUNT_API_CERT_KEY");
  return missing;
};

export const fetchEcountZone = async (config: EcountConfig) => {
  const response = await postJson(config.zoneUrl, {
    COM_CODE: config.comCode,
  });
  ensureSuccess("ZONE", response);

  const zone =
    pickFirstString(response, ["Data.Datas.ZONE", "Data.ZONE", "ZONE"]) || "";

  if (!zone) {
    throw new Error("ZONE 값을 응답에서 찾지 못했습니다.");
  }

  return { zone, response };
};

export const loginEcountServer = async (
  config: EcountConfig,
  zone: string
) => {
  const response = await postJson(
    `${config.apiHostPrefix}${zone}.ecount.com/OAPI/V2/OAPILogin`,
    {
      COM_CODE: config.comCode,
      USER_ID: config.userId,
      API_CERT_KEY: config.apiCertKey,
      LAN_TYPE: config.lanType,
      ZONE: zone,
    }
  );
  ensureSuccess("로그인", response);

  const sessionId =
    pickFirstString(response, [
      "Data.Datas.SESSION_ID",
      "Data.SESSION_ID",
      "SESSION_ID",
    ]) || "";

  if (!sessionId) {
    throw new Error("SESSION_ID 값을 응답에서 찾지 못했습니다.");
  }

  return { sessionId, response };
};

export const fetchEcountWarehouseInventory = async ({
  config,
  zone,
  sessionId,
  request,
}: {
  config: EcountConfig;
  zone: string;
  sessionId: string;
  request: EcountInventoryRequest;
}) => {
  const url =
    `${config.apiHostPrefix}${zone}.ecount.com` +
    `/OAPI/V2/InventoryBalance/GetListInventoryBalanceStatusByLocation` +
    `?SESSION_ID=${encodeURIComponent(sessionId)}`;

  const response = await postJson(url, {
    SESSION_ID: sessionId,
    BASE_DATE: request.baseDate,
    WH_CD: request.whCd || "",
    PROD_CD: request.prodCd || "",
    BAL_FLAG: request.balFlag || "N",
    DEL_GUBUN: request.delGubun || "N",
    DEL_LOCATION_YN: request.delLocationYn || "N",
    LAN_TYPE: config.lanType,
  });

  ensureSuccess("창고재고", response);
  return response;
};

export const runEcountWarehouseInventoryFlow = async ({
  config,
  request,
}: {
  config: EcountConfig;
  request: EcountInventoryRequest;
}): Promise<EcountWarehouseInventoryFlowResult> => {
  const { zone, response: zoneResponse } = await fetchEcountZone(config);
  const { sessionId, response: loginResponse } = await loginEcountServer(
    config,
    zone
  );
  const inventoryResponse = await fetchEcountWarehouseInventory({
    config,
    zone,
    sessionId,
    request,
  });

  return {
    zone,
    sessionId,
    zoneResponse,
    loginResponse,
    inventoryResponse,
  };
};

export const extractEcountInventoryRows = (response: any) =>
  pickFirstArray(response, [
    "Data.Result",
    "Data.Datas.Result",
    "Data.Datas",
    "Result",
    "Datas.Result",
  ]);

export const extractEcountTotalCount = (response: any) => {
  const value = pickFirstValue(response, [
    "Data.TotalCnt",
    "Data.Datas.TotalCnt",
    "TotalCnt",
  ]);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};
